import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import { useUpdateDarkModeMutation } from '../services/userApi';

export const ThemeToggle: React.FC = () => {
  const selectAccount = (state: RootState) => state.account;
  const { user, loggedIn } = useSelector(selectAccount);
  const [updateDarkMode] = useUpdateDarkModeMutation();

  const [isDark, setIsDark] = useState(() => {
    // Use saved preference if present, otherwise system preference
    const saved = localStorage.getItem('theme');
    if (saved === 'dark') return true;
    if (saved === 'light') return false;
    const systemPrefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    // Persist the initial choice so future loads are consistent until user changes it
    localStorage.setItem('theme', systemPrefersDark ? 'dark' : 'light');
    return systemPrefersDark;
  });

  // Apply user's saved preference when they log in
  useEffect(() => {
    if (user && typeof user.useDarkMode === 'boolean') {
      const localTheme = localStorage.getItem('theme');
      const localIsDark = localTheme === 'dark';
      
      // If user changed theme before logging in, honor that choice and save it
      if (localIsDark !== user.useDarkMode) {
        setIsDark(localIsDark);
        // Save the user's pre-login preference to backend
        updateDarkMode({ useDarkMode: localIsDark }).catch(error => {
          console.error('Failed to save dark mode preference:', error);
        });
      } else {
        // Use the user's saved preference from backend
        setIsDark(user.useDarkMode);
        localStorage.setItem('theme', user.useDarkMode ? 'dark' : 'light');
      }
    }
  }, [user]);

  useEffect(() => {
    // Apply theme to document
    if (isDark) {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const toggleTheme = async () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    
    // Save to backend if user is logged in
    if (loggedIn) {
      try {
        await updateDarkMode({ useDarkMode: newTheme }).unwrap();
      } catch (error) {
        console.error('Failed to save dark mode preference:', error);
      }
    }
  };

  return (
    <button
      className="button is-small theme-toggle-button"
      onClick={toggleTheme}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      style={{
        color: isDark ? '#e0e0e0' : 'rgb(157, 157, 157)'
      }}
    >
      <span className="icon">
        {isDark ? (
          // Sun icon for light mode
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
        ) : (
          // Moon icon for dark mode
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        )}
      </span>
    </button>
  );
};
